import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {RedirectService, StorageService, UserHttpService} from '@core/services';
import {needsBasicProfile} from '@core/utils/needs-basic-profile.util';
import {FirebaseAuthentication, User} from '@capacitor-firebase/authentication';

@Injectable({
  providedIn: 'root'
})
export class GuestOrAuthGuard {
  private userHttpService = inject(UserHttpService);
  private redirectService = inject(RedirectService);
  private router = inject(Router);
  private storageService = inject(StorageService);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    try {
      const guestMode = await this.storageService.get('guestMode');
      if (guestMode) {
        return true;
      }

      const storedUser = await this.storedUser();
      if (storedUser) {
        const cached = await this.userHttpService.readCachedSession();
        if (cached && !needsBasicProfile(cached)) {
          this.userHttpService.openFromCache(storedUser, cached);
          return true;
        }
      }

      const firebaseUser = storedUser ? null : await this.resolveFirebaseUser();
      const user = storedUser ?? firebaseUser;
      if (user) {
        const clientUser = await this.userHttpService.ensureSession(user);
        if (!clientUser) {
          return this.loginUrl(route, state);
        }
        if (needsBasicProfile(clientUser)) {
          await this.redirectService.setRedirectUrl(state.url);
          return this.router.createUrlTree(['/set-basic-profile']);
        }
        return true;
      }

      return this.loginUrl(route, state);
    } catch (error) {
      console.error('Error during guest-or-auth guard', error);
      return this.loginUrl(route, state);
    }
  }

  private async storedUser(): Promise<User | null> {
    const stored = await this.storageService.get('user');
    return stored?.uid ? stored : null;
  }

  private async loginUrl(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    await this.redirectService.setRedirectUrl(state.url);
    return this.router.createUrlTree(['/login'], {
      queryParams: {
        nextPageUrl: state.url,
        page: route?.routeConfig?.path
      }
    });
  }

  private async resolveFirebaseUser(attempts = 5, delayMs = 200): Promise<User | null> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      const result = await FirebaseAuthentication.getCurrentUser();
      if (result.user) {
        return result.user;
      }
      if (attempt < attempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    return null;
  }
}
